import * as Sentry from "@sentry/nuxt";

const config = useRuntimeConfig().public;

/**
 * Sentry reports from production only. Development and deploy builds carry a
 * DSN too, so without this check their errors landed in Sentry alongside real
 * customer failures. Not initialising at all — rather than `enabled: false` —
 * also keeps the tracing integration from instrumenting those builds.
 */
if (config.appEnv === "production") {
// @ts-expect-error - Sentry SDK v8 'init' might not be recognized in the client config context
Sentry.init({
  dsn: config.sentryDsnPublic as string,
  integrations: [
    // @ts-expect-error - Sentry SDK v8 browserTracingIntegration type might not be recognized
    Sentry.browserTracingIntegration(),
    // Session Replay is intentionally NOT loaded: its worker is one of the
    // heaviest things in the client bundle and it ran on every page (including
    // the marketing landing). Dropping the `replayIntegration()` call lets the
    // bundler tree-shake the replay code out entirely. To restore it without
    // re-bloating the initial bundle, lazy-load it after idle:
    //   Sentry.lazyLoadIntegration("replayIntegration").then(r => Sentry.addIntegration(r()))
  ],
  environment: config.appEnv as string,
  tracePropagationTargets: [config.frontendUrl as string],
  tracesSampleRate: config.sentryTracesSampleRate as unknown as number,
  /**
   * Instagram's in-app browser injects a navigation performance logger that
   * posts to the native app. When the page navigates or closes that bridge is
   * gone and the script throws. None of it is our code and none of it affects
   * the visitor.
   *
   * On Android the script is served from `iabjs://`. `denyUrls` matches the
   * frame the error was thrown in — the last one that is not `<anonymous>` —
   * which is what makes it reliable. The filter this replaces required *every*
   * frame to be Instagram's, and failed whenever Sentry's own `sentryWrapped`
   * frame sat at the bottom of the stack, which it does for every error caught
   * inside an event listener.
   */
  denyUrls: [/^iabjs:\/\//],
  /**
   * The same logger on iOS is evaluated into the page, so its frames carry our
   * own URL and `denyUrls` cannot tell it apart; its message can. Our code never
   * touches `window.webkit.messageHandlers` or Android's Java bridge. The
   * `postMessage` pattern also backs up `denyUrls` for events that arrive with
   * no stack.
   */
  ignoreErrors: [
    /window\.webkit\.messageHandlers/,
    /Error invoking postMessage: Java (object is gone|exception was raised)/,
  ],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  beforeSend(event: any, hint: any) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const originalException = hint?.originalException as any;
    const nuxtErrorCode =
      originalException?.statusCode || originalException?.cause?.statusCode;
    const backendErrorCode = originalException?.data?.code;

    /**
     * Statuses that mean the request was answered correctly and there is
     * nothing to fix: an unknown storefront, a signed-out visitor, a page they
     * may not see. Bots produce these all day.
     *
     * 400 is deliberately NOT in this list. It reads like a client mistake, but
     * the API never uses it for one — bad input comes back as 422. Its handler
     * maps every *unexpected* failure onto 400 "Something went wrong": a PHP
     * error, a database failure, a throttled request. Dropping 400 here is what
     * made real server faults invisible from the frontend side.
     */
    if ([401, 403, 404].includes(nuxtErrorCode || backendErrorCode)) {
      return null;
    }
    return event;
  },
});
}
